import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarMonth } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Autocomplete, Box, Button, DialogActions, DialogContent, InputAdornment, TextField } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { t } from "i18next";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo } from "react";
import { Control, Controller, FieldErrors, FieldValues, Path, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TbBrandDaysCounter, TbCar, TbDatabase, TbInfoCircle, TbNotes, TbUser } from "react-icons/tb";
import { z } from "zod";

import { CommonDatePicker, CommonInput, CommonLabel } from "@/components/CommonInput";
import PlateComponent from "@/components/Plate";
import { driverStatuses } from "@/constants/driverStatuses";
import { DRIVER_STATUS } from "@/constants/status";
import { splitPlate } from "@/helpers/strings";
import { useCreateShiftMutation, useUpdateShiftMutation } from "@/requests/daily-status.queries";
import { DriverStatus } from "@/types/daily-status.types";
import { UserExtras, UserType } from "@/types/user.types";
import { WeTrackVehicleData } from "@/types/wetrack.types";

import { useDriverStatusContext } from "../DriverStatusContext";
import { DriverStatusType } from "../types";
import DriverStatusTooltip from "./DriverStatusTooltip";

const getStartDate = (month: DateTime<true>, day: number) =>
    month.set({ day, hour: 9, minute: 0, second: 0, millisecond: 0 });
const getEndDate = (month: DateTime<true>, day: number) =>
    month.set({ day, hour: 18, minute: 0, second: 0, millisecond: 0 });

const driverStatusFormSchema = z
    .object({
        userId: z.string().min(1),
        status: z.enum([
            DRIVER_STATUS.WORKING,
            DRIVER_STATUS.FREE_DAY,
            DRIVER_STATUS.EXCUSED_LEAVE,
            DRIVER_STATUS.PUBLIC_HOLIDAY,
            DRIVER_STATUS.UNPAID_LEAVE,
            DRIVER_STATUS.ANNUAL_LEAVE,
            DRIVER_STATUS.NONE,
            DRIVER_STATUS.OVERTIME_PERMIT,
            DRIVER_STATUS.SICK_LEAVE,
        ]),
        note: z.string(),
        dateRange: z.tuple([
            z.custom<DateTime | null>((val) => val !== null && val instanceof DateTime, {
                message: "startTime cannot be null and must be a valid date",
            }),
            z.custom<DateTime | null>(),
        ]),
        vehicleId: z.number().int().positive().optional(),
        userType: z.nativeEnum(UserType).default(UserType.DRIVER),
        extras: z.number(),
    })
    .superRefine((data, ctx) => {
        if (data.userType === UserType.DRIVER) {
            const extras = data.extras || 0;
            if (
                data.status === DRIVER_STATUS.WORKING &&
                !(extras & UserExtras.SHIFT_WITHOUT_VEHICLE) &&
                !data.vehicleId
            ) {
                ctx.addIssue({
                    path: ["vehicleId"],
                    message: "Şoför için araçlı mesai seçildiğinden araç zorunludur.",
                    code: z.ZodIssueCode.custom,
                });
            }
        }
        const [startTime, endTime] = data.dateRange;
        if (startTime && !startTime.hasSame(DateTime.local(), "day") && !endTime) {
            ctx.addIssue({
                path: ["dateRange"],
                message: "endTime cannot be null unless startTime is today's date",
                code: z.ZodIssueCode.custom,
            });
        }
    });

export type IDriverStatusForm = z.infer<typeof driverStatusFormSchema>;

const DriverStatusInfoPopup = ({
    closePopup,
    row,
    day,
    activeState,
}: {
    row: DriverStatusType;
    closePopup: () => void;
    day: number;
    activeState: DriverStatus | null;
}) => {
    const { t, i18n } = useTranslation();
    const { month, vehicles } = useDriverStatusContext();
    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
        setValue,
        control,
        trigger,
    } = useForm<IDriverStatusForm>({
        resolver: zodResolver(driverStatusFormSchema),
        defaultValues: {
            status: activeState?.status || DRIVER_STATUS.FREE_DAY,
            note: activeState?.note || "",
            dateRange: activeState
                ? [activeState.startTime, activeState.endTime]
                : [getStartDate(month, day), getEndDate(month, day)],
            userId: row.id as string,
            vehicleId: activeState?.vehicleId || undefined,
            userType: (row.userType as UserType) || UserType.DRIVER,
            extras: row.extras,
        },
    });

    const { mutateAsync: createShift, isPending: createLoading } = useCreateShiftMutation();
    const { mutateAsync: updateShift, isPending: updateLoading } = useUpdateShiftMutation();
    const loading = useMemo(() => createLoading || updateLoading, [createLoading, updateLoading]);

    const status = watch("status") as IDriverStatusForm["status"];
    const vehicleId = watch("vehicleId");
    const dateRange = watch("dateRange");
    const userType = watch("userType");
    const extras = watch("extras");

    useEffect(() => {
        if (!activeState) {
            if (status !== DRIVER_STATUS.WORKING) {
                setValue("vehicleId", undefined);
                setValue("dateRange", [getStartDate(month, day), getEndDate(month, day)]);
            } else {
                setValue("vehicleId", row.vehicleId);
            }
            trigger("vehicleId");
        }
    }, [status, trigger]);

    const hours = useMemo(() => {
        const [startTime, endTime] = dateRange || ([] as unknown as [DateTime, DateTime]);

        if (!startTime || !endTime) return "";

        const diff = endTime.diff(startTime, ["hours", "minutes"]);

        return `${Math.floor(diff.hours)}h ${Math.round(diff.minutes)}m`;
    }, [dateRange]);

    const onSubmit = async (data: IDriverStatusForm) => {
        try {
            if (loading) return;
            if (activeState) {
                await updateShift({ ...data, id: activeState.id });
                enqueueSnackbar(t("driverStatusUpdated"), { variant: "success" });
            } else {
                await createShift(data);
                enqueueSnackbar(t("driverStatusCreated"), { variant: "success" });
            }
            closePopup();
        } catch (error) {
            console.error("Registration error:", error);
        }
    };
    return (
        <>
            <DialogContent dividers>
                <form autoComplete="off" onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-10 gap-7">
                    <div className="col-span-10 grid grid-cols-10">
                        <CommonLabel label={t("driver")} className=" col-span-4">
                            <CommonInput
                                value={row.name}
                                disabled
                                startAdornment={
                                    <InputAdornment position="start">
                                        <TbUser />
                                    </InputAdornment>
                                }
                            ></CommonInput>
                        </CommonLabel>
                    </div>
                    {status === DRIVER_STATUS.WORKING &&
                        !(extras && extras & UserExtras.SHIFT_WITHOUT_VEHICLE) &&
                        userType === UserType.DRIVER && (
                            <VehicleField
                                vehicles={vehicles}
                                loading={loading}
                                errors={errors}
                                control={control}
                                vehicleId={vehicleId}
                            />
                        )}
                    <StatusField
                        isUpdate={!!activeState}
                        loading={loading}
                        status={status}
                        errors={errors}
                        control={control}
                    />
                    <CommonLabel description={t("automaticCalculates")} label={t("hour")} className=" col-span-3">
                        <CommonInput
                            value={hours}
                            disabled
                            endAdornment={
                                <InputAdornment position="end">
                                    <TbBrandDaysCounter size={24} />
                                </InputAdornment>
                            }
                        ></CommonInput>
                    </CommonLabel>
                    <CommonLabel label={t("startAndDate")} className="col-span-10">
                        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
                            <Controller
                                name="dateRange"
                                control={control}
                                render={({ field }) => (
                                    <CommonDatePicker
                                        {...field}
                                        localeText={{ start: "", end: "" }}
                                        ampm={false}
                                        disabled={loading}
                                        slotProps={{
                                            textField: () => ({
                                                variant: "filled",
                                                error: !!errors?.dateRange,
                                                helperText:
                                                    errors?.dateRange &&
                                                    (errors?.dateRange[0]?.message ||
                                                        errors?.dateRange?.message ||
                                                        errors?.dateRange[1]?.message),
                                                InputProps: {
                                                    startAdornment: (
                                                        <InputAdornment className=" -translate-y-2" position="start">
                                                            <CalendarMonth fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                },
                                            }),
                                        }}
                                        onChange={(value) => field.onChange(value)}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </CommonLabel>
                    <CommonLabel label={t("description")} className=" col-span-10">
                        <CommonInput
                            placeholder={t("placeholders.enter", { field: t("description") })}
                            multiline
                            {...register("note")}
                            rows={3}
                            classes={{
                                root: "!py-2",
                            }}
                            disabled={loading}
                            error={!!errors?.status}
                            endAdornment={
                                <InputAdornment position="end">
                                    <TbNotes />
                                </InputAdornment>
                            }
                        ></CommonInput>
                    </CommonLabel>
                </form>
            </DialogContent>
            <DialogActions>
                <Button autoFocus disabled={loading} color="secondary" variant="outlined" onClick={closePopup}>
                    {t("buttons.cancel")}
                </Button>

                <LoadingButton
                    loading={loading}
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit(onSubmit)}
                >
                    {t(activeState ? "buttons.update" : "buttons.create")}
                </LoadingButton>
            </DialogActions>
        </>
    );
};

export function VehicleField<T extends FieldValues>({
    control,
    vehicleId,
    errors,
    loading,
    vehicles,
}: {
    control: Control<T>;
    errors: FieldErrors<{ vehicleId: number | undefined }>;
    vehicleId: number | undefined;
    loading: boolean;
    vehicles: WeTrackVehicleData["properties"][];
}) {
    const activeVehicle = useMemo(
        () => vehicles.find(({ vehicle_id }) => vehicle_id === vehicleId) || null,
        [vehicleId, vehicles]
    );

    return (
        <CommonLabel label={t("vehicle")} className=" col-span-7">
            <Controller
                name={"vehicleId" as Path<T>}
                control={control}
                render={({ field }) => (
                    <Autocomplete
                        {...field}
                        classes={{
                            inputRoot: " !h-[47px]",
                        }}
                        sx={() => ({
                            "label + &": {
                                marginTop: 20,
                            },
                            "& .MuiInputBase-input": {
                                paddingBottom: 12,
                                paddingTop: 12,
                            },
                        })}
                        value={activeVehicle}
                        options={vehicles}
                        disabled={loading}
                        onChange={(_, value) => field.onChange(value?.vehicle_id)}
                        getOptionLabel={({ plate }) => splitPlate(plate)}
                        renderOption={(props, { plate }) => {
                            const { key, ...optionProps } = props;

                            return (
                                <Box
                                    key={key}
                                    component="li"
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                    }}
                                    {...optionProps}
                                >
                                    <PlateComponent plate={plate} />
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={t("placeholders.select", { field: t("vehicle") })}
                                error={!!errors?.vehicleId}
                                helperText={errors?.vehicleId?.message}
                                variant="filled"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        transform: "translateY(-8px)",
                                    },
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="end" className="  !-translate-x-1 !-translate-y-2">
                                            <TbCar />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
            />
        </CommonLabel>
    );
}

const StatusField = ({
    control,
    errors,
    status,
    loading,
    isUpdate,
}: {
    control: Control<IDriverStatusForm>;
    errors: FieldErrors<IDriverStatusForm>;
    status: IDriverStatusForm["status"];
    loading: boolean;
    isUpdate: boolean;
}) => {
    const { t } = useTranslation();
    const activeStatus = useMemo(
        () => driverStatuses.find((item) => item.status === status) || null,
        [driverStatuses, status]
    );
    return (
        <CommonLabel label="Veri Türü" className=" col-span-7">
            <Controller
                name="status"
                control={control}
                render={({ field }) => (
                    <Autocomplete
                        {...field}
                        disabled={loading || isUpdate}
                        classes={{
                            inputRoot: " !h-[47px]",
                        }}
                        sx={() => ({
                            "label + &": {
                                marginTop: 20,
                            },
                            "& .MuiInputBase-input": {
                                paddingBottom: 12,
                                paddingTop: 12,
                            },
                        })}
                        renderOption={(props, option) => {
                            const { key, ...optionProps } = props;

                            return (
                                <Box key={key} component="li" className="flex items-center gap-1" {...optionProps}>
                                    {t("driverStatuses." + option.status)}
                                    <DriverStatusTooltip
                                        child={
                                            <div className="max-w-fit max-h-fit !ml-auto">
                                                <TbInfoCircle />
                                            </div>
                                        }
                                        status={option}
                                    />
                                </Box>
                            );
                        }}
                        value={activeStatus}
                        getOptionLabel={({ status }) => t("driverStatuses." + status)}
                        options={driverStatuses}
                        onChange={(_, value) => field.onChange(value?.status)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                error={!!errors?.status}
                                helperText={errors?.status?.message}
                                variant="filled"
                                placeholder={t("placeholders.enter", { field: t("dataType") })}
                                sx={{
                                    "& .MuiInputBase-input": {
                                        transform: "translateY(-8px)",
                                    },
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="end" className=" !-translate-x-1 !-translate-y-2">
                                            <TbDatabase />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
            />
        </CommonLabel>
    );
};

export default DriverStatusInfoPopup;
